'use client'
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import '@splidejs/react-splide/css/core';
import "./project-splide.css";


/**
 * @typedef {import("@prismicio/client").Content.ProjectsPreviewSlice} ProjectsPreviewSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectsPreviewSlice>} ProjectsPreviewProps
 * @type {import("react").FC<ProjectsPreviewProps>}
 */
const ProjectsPreview = ({ slice, index, slices }) => {
  const isLast = Array.isArray(slices) && index === slices.length - 1;
  const fallbackAlt = "Image de contenu";

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white`}
      id="projets"
    >
      {slice.variation === "default" && (
        <div className={`h-[100dvh] pt-[76px] ${isLast ? "md:pb-10" : ""}`}>
          {isFilled.group(slice.primary.projects) && slice.primary.projects.length > 0 && (
            <>
              <div className="flex h-full overflow-x-auto snap-x snap-mandatory no-scrollbar lg:hidden">
                {slice.primary.projects.map((item, index) => (
                  <PrismicNextLink
                    field={item.project}
                    key={index}
                    className="relative h-full overflow-hidden shrink-0 snap-start w-[85vw]"
                  >
                    <div className="flex gap-4 pl-2 w-full items-center justify-end relative z-10">
                      <h2 className="text-white font-neue-haas text-base uppercase font-medium max-h-8">{item.project.data.preview_name}</h2>
                      <div className="bg-white aspect-square h-8 w-9 flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[10px] h-[10px]">
                          <g clipPath="url(#clip0_58_6)">
                            <path d="M0 4.42H4.25V0H4.91V4.42H9.16V5.06H4.91V9.53H4.25V5.06H0V4.42Z" fill="black" />
                          </g>
                          <defs>
                          </defs>
                        </svg>
                      </div>
                    </div>
                    <PrismicNextImage
                      field={item.project.data?.img}
                      alt={item.project.data?.img?.alt || fallbackAlt}
                      className="w-full h-full absolute top-0 object-cover z-0"
                    />
                  </PrismicNextLink>
                ))}
              </div>

              <div className="hidden lg:block h-full">
                <Splide
                  options={{
                    type: "slide",
                    autoWidth: true,
                    pagination: false,
                    arrows: false,
                    drag: true,
                    snap: true,
                    gap: "0rem",
                  }}
                  className="h-full project-preview-splide"
                  aria-label="Aperçu des projets"
                >
                  {slice.primary.projects.map((item, index) => (
                    <SplideSlide key={index} className="!h-full !w-[38vw]">
                      <PrismicNextLink
                        field={item.project}
                        className="relative h-full group overflow-hidden shrink-0 snap-start w-[38vw] block"
                      >
                        <div className="flex gap-4 pl-2 w-full items-center justify-end relative z-10">
                          <h2 className="font-neue-haas text-white text-base uppercase font-medium max-h-8">{item.project.data.preview_name}</h2>
                          <div className="bg-white aspect-square h-8 w-9 flex items-center justify-center">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[10px] h-[10px]">
                              <g clipPath="url(#clip0_58_6)">
                                <path d="M0 4.42H4.25V0H4.91V4.42H9.16V5.06H4.91V9.53H4.25V5.06H0V4.42Z" fill="black" />
                              </g>
                              <defs>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <PrismicNextImage
                          field={item.project.data?.img}
                          alt={item.project.data?.img?.alt || fallbackAlt}
                          className="w-full h-full absolute top-0 object-cover z-0 group-hover:scale-105 transition-all! duration-500"
                        />
                      </PrismicNextLink>
                    </SplideSlide>
                  ))}
                </Splide>
              </div>
            </>
          )}
        </div>
      )}

      {slice.variation === "simple" && (
        <div className={`${isLast ? "md:pb-10" : ""} pt-4 `}>
          {isFilled.richText(slice.primary.title) && (
            <div className="flex items-end gap-8  px-4 md:px-10 mb-4">
              <div className="flex items-center w-full gap-8">
                <div className="bg-gray w-full h-[1px] hidden md:block"></div>
                <PrismicRichText
                  field={slice.primary.title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="project-title md:whitespace-nowrap">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="project-title md:whitespace-nowrap">{children}</h3>
                    ),
                  }}
                />
              </div>
              <img src="/mw-arrow.svg" alt="flèche" className="w-16 lg:w-30 mb-2" />
            </div>
          )}
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar lg:overflow-visible lg:snap-none">
            {slice.primary.projects.map((item, index) => (
              <PrismicNextLink
                field={item.project}
                key={index}
                className="relative min-h-100 md:min-h-120 xl:min-h-160 group overflow-hidden shrink-0 snap-start w-[80vw] md:w-[45vw] lg:w-auto lg:flex-1"
              >
                <div className="flex gap-4 pl-2 w-full items-center justify-end relative z-10">
                  <h2 className="text-white font-neue-haas text-base uppercase font-medium max-h-8">{item.project.data.preview_name}</h2>
                  <div className="bg-white aspect-square h-8 w-9 flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[10px] h-[10px]">
                      <g clipPath="url(#clip0_58_6)">
                        <path d="M0 4.42H4.25V0H4.91V4.42H9.16V5.06H4.91V9.53H4.25V5.06H0V4.42Z" fill="black" />
                      </g>
                      <defs>
                      </defs>
                    </svg>
                  </div>
                </div>
                <PrismicNextImage
                  field={item.project.data?.img}
                  alt={item.project.data?.img?.alt || fallbackAlt}
                  className="w-full h-full absolute top-0 object-cover z-0 group-hover:scale-110 transition-all duration-500"
                />
              </PrismicNextLink>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectsPreview;
